// ============================================================
// FILE LOCATION: src/components/TimelineBuilder/suggestionEngine.ts
// ============================================================

/**
 * Suggestion Engine
 * Analyzes timeline content and generates actionable appeal coaching recommendations
 */

import { TimelineEvent, CaseType, TimelineEventType } from "./types";

// ============================================================
// Types
// ============================================================

export interface AppealSuggestion {
  id: string;
  priority: number; // 1 = highest
  priorityLabel: "high" | "medium" | "low"; // For UI badge
  category: SuggestionCategory;
  title: string;
  previewText: string; // Short context shown when collapsed
  whyItMatters: string;
  whatToAdd: string[];
  example?: string;
  action: SuggestionAction;
}

export type SuggestionCategory =
  | "chronology"
  | "conservative_therapy"
  | "objective_evidence"
  | "denial_response"
  | "functional_impact";

export interface SuggestionAction {
  label: string;
  eventTemplate: EventTemplate;
}

export interface EventTemplate {
  event_type: TimelineEventType;
  title: string;
  placeholder?: string;
  outcome?: string;
  focusField?: "title" | "details" | "date";
}

// ============================================================
// Event Templates Library
// ============================================================

export const EVENT_TEMPLATES: Record<string, EventTemplate> = {
  conservative_therapy: {
    event_type: "CONSERVATIVE_THERAPY",
    title: "Conservative therapy trial",
    placeholder: "e.g., PT, chiropractic, activity modification",
    focusField: "title",
  },
  medication_trial: {
    event_type: "MEDICATION",
    title: "Medication trial",
    placeholder: "e.g., NSAIDs x 6 weeks, gabapentin 300mg TID",
    focusField: "title",
  },
  pt_ot_trial: {
    event_type: "CONSERVATIVE_THERAPY",
    title: "PT/OT course",
    placeholder: "e.g., 8 weeks PT, 2x/week",
    focusField: "title",
  },
  imaging_result: {
    event_type: "IMAGING",
    title: "Imaging findings",
    placeholder: "e.g., MRI showed...",
    focusField: "details",
  },
  specialist_visit: {
    event_type: "CONSULT",
    title: "Specialist evaluation",
    placeholder: "e.g., Pain management consult",
    focusField: "title",
  },
  functional_impact: {
    event_type: "FUNCTION_CHANGE",
    title: "Functional limitation",
    placeholder: "e.g., Unable to work full shifts, limited ADLs",
    focusField: "details",
  },
  adverse_effect: {
    event_type: "ADVERSE_EVENT",
    title: "Adverse effect / intolerance",
    placeholder: "e.g., GI bleeding on NSAIDs, sedation on gabapentin",
    focusField: "details",
  },
  clinical_rationale: {
    event_type: "FOLLOWUP",
    title: "Clinical rationale",
    placeholder: "Why this treatment is appropriate given history",
    focusField: "details",
  },
  symptom_onset: {
    event_type: "SYMPTOM",
    title: "Symptom onset",
    placeholder: "When symptoms began",
    focusField: "date",
  },
  prior_procedure: {
    event_type: "PROCEDURE",
    title: "Prior procedure",
    placeholder: "e.g., Injection, nerve block",
    focusField: "title",
  },
  denial_received: {
    event_type: "DENIAL",
    title: "Denial received",
    placeholder: "Reason for denial",
    focusField: "details",
  },
  appeal_submitted: {
    event_type: "PRIOR_AUTH",
    title: "Appeal submitted",
    placeholder: "Appeal details",
    focusField: "details",
  },
  peer_to_peer: {
    event_type: "FOLLOWUP",
    title: "Peer-to-peer completed",
    placeholder: "Outcome of P2P call",
    focusField: "details",
  },
};

// ============================================================
// Analysis Helpers
// ============================================================

interface TimelineAnalysis {
  hasSymptomOnset: boolean;
  hasConservativeTherapy: boolean;
  hasMedicationTrials: boolean;
  hasPTOT: boolean;
  hasImagingOrLabs: boolean;
  hasFunctionalImpact: boolean;
  hasDenial: boolean;
  hasCurrentRequest: boolean;
  hasEvidenceTags: boolean;
  hasOutcomes: boolean;
  eventCount: number;
  therapyEventsWithoutOutcome: number;
  eventsWithoutDates: number;
  denialReason: string | null;
}

function analyzeTimeline(events: TimelineEvent[]): TimelineAnalysis {
  const analysis: TimelineAnalysis = {
    hasSymptomOnset: false,
    hasConservativeTherapy: false,
    hasMedicationTrials: false,
    hasPTOT: false,
    hasImagingOrLabs: false,
    hasFunctionalImpact: false,
    hasDenial: false,
    hasCurrentRequest: false,
    hasEvidenceTags: false,
    hasOutcomes: false,
    eventCount: events.length,
    therapyEventsWithoutOutcome: 0,
    eventsWithoutDates: 0,
    denialReason: null,
  };

  for (const event of events) {
    // Check event types
    switch (event.event_type) {
      case "SYMPTOM":
        analysis.hasSymptomOnset = true;
        break;
      case "CONSERVATIVE_THERAPY":
        analysis.hasConservativeTherapy = true;
        // Check if PT/OT specifically
        if (
          event.title?.toLowerCase().includes("pt") ||
          event.title?.toLowerCase().includes("physical therapy") ||
          event.title?.toLowerCase().includes("ot") ||
          event.title?.toLowerCase().includes("occupational therapy")
        ) {
          analysis.hasPTOT = true;
        }
        break;
      case "MEDICATION":
        analysis.hasMedicationTrials = true;
        break;
      case "IMAGING":
      case "LAB":
        analysis.hasImagingOrLabs = true;
        break;
      case "FUNCTION_CHANGE":
        analysis.hasFunctionalImpact = true;
        break;
      case "DENIAL":
        analysis.hasDenial = true;
        analysis.denialReason = event.details || null;
        break;
      case "REQUEST":
        analysis.hasCurrentRequest = true;
        break;
    }

    // Check for evidence tags
    if (event.evidence_flags && event.evidence_flags.length > 0) {
      analysis.hasEvidenceTags = true;
    }

    // Check for outcomes on therapy events
    if (
      event.event_type === "CONSERVATIVE_THERAPY" ||
      event.event_type === "MEDICATION"
    ) {
      if (event.outcome && event.outcome !== "PENDING") {
        analysis.hasOutcomes = true;
      } else {
        analysis.therapyEventsWithoutOutcome++;
      }
    }

    // Check for missing dates
    if (!event.date_label && !event.start_date) {
      analysis.eventsWithoutDates++;
    }
  }

  return analysis;
}

// ============================================================
// Suggestion Generators
// ============================================================

function generateConservativeTherapySuggestions(
  analysis: TimelineAnalysis,
  caseType: CaseType
): AppealSuggestion[] {
  const suggestions: AppealSuggestion[] = [];

  // Priority 1: No conservative therapy at all
  if (!analysis.hasConservativeTherapy && !analysis.hasMedicationTrials) {
    suggestions.push({
      id: "missing_conservative_therapy",
      priority: 1,
      priorityLabel: "high",
      category: "conservative_therapy",
      title: "Document conservative therapies tried",
      previewText: "No conservative therapy events documented",
      whyItMatters:
        "Insurers often expect documentation of stepwise treatment attempts before authorizing escalation.",
      whatToAdd: [
        "Prior therapies tried (medications, PT, activity modification)",
        "Duration of each trial (e.g., 6–8 weeks)",
        "Response or reason for discontinuation",
      ],
      example:
        'e.g., "Completed 8 weeks of PT with no improvement in pain or function"',
      action: {
        label: "Add conservative therapy",
        eventTemplate: EVENT_TEMPLATES.conservative_therapy,
      },
    });
  }

  // Priority 2: Has therapy but no outcomes documented
  if (
    (analysis.hasConservativeTherapy || analysis.hasMedicationTrials) &&
    analysis.therapyEventsWithoutOutcome > 0
  ) {
    suggestions.push({
      id: "missing_therapy_outcomes",
      priority: 2,
      priorityLabel: "high",
      category: "conservative_therapy",
      title: "Add outcomes for treatment trials",
      previewText: `${analysis.therapyEventsWithoutOutcome} treatment${analysis.therapyEventsWithoutOutcome > 1 ? "s" : ""} missing outcome`,
      whyItMatters:
        "Documenting treatment response demonstrates that conservative options were given adequate trial.",
      whatToAdd: [
        "Response to each therapy (improved, no change, worsened)",
        "Duration of trial before determining response",
        "Side effects or intolerances if applicable",
      ],
      example:
        'e.g., "NSAIDs x 6 weeks → no change; gabapentin → intolerable sedation"',
      action: {
        label: "Add treatment outcome",
        eventTemplate: {
          ...EVENT_TEMPLATES.medication_trial,
          outcome: "NO_CHANGE",
        },
      },
    });
  }

  // Priority 3: No PT/OT for procedure/surgery cases
  if (
    !analysis.hasPTOT &&
    (caseType === "PROCEDURE" || caseType === "SURGERY" || caseType === "IMAGING")
  ) {
    suggestions.push({
      id: "missing_pt_ot",
      priority: 3,
      priorityLabel: "medium",
      category: "conservative_therapy",
      title: "Document physical therapy history",
      previewText: "No PT/OT course documented",
      whyItMatters:
        "PT/OT trials are commonly expected before procedures or advanced imaging.",
      whatToAdd: [
        "PT/OT course duration and frequency",
        "Specific modalities used",
        "Functional outcome or plateau",
      ],
      example:
        'e.g., "Completed 6 weeks PT (2x/week) with transient relief only"',
      action: {
        label: "Add PT/OT course",
        eventTemplate: EVENT_TEMPLATES.pt_ot_trial,
      },
    });
  }

  // No medication trials for medication requests
  if (!analysis.hasMedicationTrials && caseType === "MEDICATION") {
    suggestions.push({
      id: "missing_medication_trials",
      priority: 2,
      priorityLabel: "high",
      category: "conservative_therapy",
      title: "Document prior medication trials",
      previewText: "No prior medications documented",
      whyItMatters:
        "Step therapy documentation shows that preferred alternatives were tried first.",
      whatToAdd: [
        "Medications tried (name, dose, duration)",
        "Reason for discontinuation",
        "Contraindications to alternatives if applicable",
      ],
      example:
        'e.g., "Tried ibuprofen 800mg TID x 4 weeks → GI intolerance; meloxicam → no relief"',
      action: {
        label: "Add medication trial",
        eventTemplate: EVENT_TEMPLATES.medication_trial,
      },
    });
  }

  return suggestions;
}

function generateChronologySuggestions(
  analysis: TimelineAnalysis
): AppealSuggestion[] {
  const suggestions: AppealSuggestion[] = [];

  // Missing symptom onset
  if (!analysis.hasSymptomOnset) {
    suggestions.push({
      id: "missing_symptom_onset",
      priority: 3,
      priorityLabel: "medium",
      category: "chronology",
      title: "Add symptom onset date",
      previewText: "No symptom onset anchor",
      whyItMatters:
        "Establishing when symptoms began anchors the timeline and shows duration of illness.",
      whatToAdd: [
        "Approximate date symptoms began",
        "Initial presentation or triggering event",
        "Symptom progression over time",
      ],
      example: 'e.g., "Low back pain began Jan 2025 after lifting injury"',
      action: {
        label: "Add symptom onset",
        eventTemplate: EVENT_TEMPLATES.symptom_onset,
      },
    });
  }

  // Sparse timeline (only 1-2 events)
  if (analysis.eventCount < 3 && analysis.hasCurrentRequest) {
    suggestions.push({
      id: "sparse_timeline",
      priority: 4,
      priorityLabel: "medium",
      category: "chronology",
      title: "Add prior history to show progression",
      previewText: `Only ${analysis.eventCount} event${analysis.eventCount !== 1 ? "s" : ""} in timeline`,
      whyItMatters:
        "A complete chronology demonstrates treatment progression and appropriateness of escalation.",
      whatToAdd: [
        "Key milestones in treatment history",
        "Prior interventions and their outcomes",
        "Progression or worsening over time",
      ],
      example:
        'e.g., "Symptoms Jan 2025 → NSAIDs Feb → PT Mar–Apr → persistent pain"',
      action: {
        label: "Add history event",
        eventTemplate: EVENT_TEMPLATES.conservative_therapy,
      },
    });
  }

  // Events without dates
  if (analysis.eventsWithoutDates > 1) {
    suggestions.push({
      id: "missing_dates",
      priority: 5,
      priorityLabel: "low",
      category: "chronology",
      title: "Add dates to timeline events",
      previewText: `${analysis.eventsWithoutDates} events missing dates`,
      whyItMatters:
        "Specific dates strengthen the timeline by showing treatment duration and progression.",
      whatToAdd: [
        "Approximate month/year for each event",
        "Duration of treatments (e.g., 6 weeks)",
        "Time between treatment steps",
      ],
      action: {
        label: "Review dates",
        eventTemplate: EVENT_TEMPLATES.symptom_onset,
      },
    });
  }

  return suggestions;
}

function generateFunctionalImpactSuggestions(
  analysis: TimelineAnalysis,
  caseType: CaseType
): AppealSuggestion[] {
  const suggestions: AppealSuggestion[] = [];

  if (!analysis.hasFunctionalImpact) {
    suggestions.push({
      id: "missing_functional_impact",
      priority: 2,
      priorityLabel: "high",
      category: "functional_impact",
      title: "Document functional limitations",
      previewText: "No functional impact recorded",
      whyItMatters:
        "Functional impairment helps establish urgency and medical necessity for the requested treatment.",
      whatToAdd: [
        "ADL limitations (dressing, bathing, mobility)",
        "Work restrictions or disability",
        "Sleep disruption, safety risks",
        "Quality of life impact",
      ],
      example:
        'e.g., "Pain limits standing >10 min; unable to work full shifts; waking 3x/night"',
      action: {
        label: "Add functional impact",
        eventTemplate: EVENT_TEMPLATES.functional_impact,
      },
    });
  }

  return suggestions;
}

function generateEvidenceSuggestions(
  analysis: TimelineAnalysis
): AppealSuggestion[] {
  const suggestions: AppealSuggestion[] = [];

  if (!analysis.hasEvidenceTags && analysis.eventCount > 2) {
    suggestions.push({
      id: "missing_evidence",
      priority: 4,
      priorityLabel: "low",
      category: "objective_evidence",
      title: "Tag supporting documentation",
      previewText: "No evidence tags attached",
      whyItMatters:
        "Linking events to documentation substantiates the timeline and reduces back-and-forth.",
      whatToAdd: [
        "Clinical notes for key events",
        "Imaging reports or lab results",
        "Denial letter (if appealing)",
        "PT/specialist summaries",
      ],
      example:
        'e.g., "Expand an event → Evidence section → tag relevant documents"',
      action: {
        label: "Add evidence tags",
        eventTemplate: EVENT_TEMPLATES.imaging_result,
      },
    });
  }

  // Has imaging/labs but no evidence tags
  if (analysis.hasImagingOrLabs && !analysis.hasEvidenceTags) {
    suggestions.push({
      id: "untaged_diagnostics",
      priority: 5,
      priorityLabel: "low",
      category: "objective_evidence",
      title: "Link diagnostic reports",
      previewText: "Diagnostics not linked to reports",
      whyItMatters:
        "Tagging imaging or lab events with reports makes findings verifiable.",
      whatToAdd: [
        "Imaging report reference",
        "Key findings from diagnostics",
        "Relevant lab values",
      ],
      action: {
        label: "Add diagnostic details",
        eventTemplate: EVENT_TEMPLATES.imaging_result,
      },
    });
  }

  return suggestions;
}

function generateDenialResponseSuggestions(
  analysis: TimelineAnalysis
): AppealSuggestion[] {
  const suggestions: AppealSuggestion[] = [];

  // Has denial but timeline doesn't clearly address it
  if (analysis.hasDenial && !analysis.hasOutcomes && !analysis.hasConservativeTherapy) {
    suggestions.push({
      id: "denial_not_addressed",
      priority: 1,
      priorityLabel: "high",
      category: "denial_response",
      title: "Address the denial reason in your timeline",
      previewText: "Denial reason not addressed",
      whyItMatters:
        "Your timeline should directly demonstrate why the denial reason doesn't apply to this case.",
      whatToAdd: [
        "Events showing criteria are met",
        "Documentation of required steps",
        "Clinical rationale for why request is appropriate",
      ],
      example:
        'e.g., If denied for "no conservative therapy," add PT and medication trial events',
      action: {
        label: "Add clinical rationale",
        eventTemplate: EVENT_TEMPLATES.clinical_rationale,
      },
    });
  }

  return suggestions;
}

// ============================================================
// Main Export
// ============================================================

export function generateSuggestions(
  events: TimelineEvent[],
  caseType: CaseType,
  maxSuggestions: number = 6
): AppealSuggestion[] {
  const analysis = analyzeTimeline(events);

  // Collect all suggestions
  const allSuggestions: AppealSuggestion[] = [
    ...generateConservativeTherapySuggestions(analysis, caseType),
    ...generateChronologySuggestions(analysis),
    ...generateFunctionalImpactSuggestions(analysis, caseType),
    ...generateEvidenceSuggestions(analysis),
    ...generateDenialResponseSuggestions(analysis),
  ];

  // Sort by priority and limit
  return allSuggestions
    .sort((a, b) => a.priority - b.priority)
    .slice(0, maxSuggestions);
}

export function getEventTemplate(templateKey: string): EventTemplate | null {
  return EVENT_TEMPLATES[templateKey] || null;
}